import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import SeoHead from '../../components/Seo/SeoHead';
import { characterApi } from '../../services/characterApi';
import { getCharacterProfilePath, getCharacterSheetPath } from '../../utils/characterPaths';
import { normalizeSheetTheme, useCharacterSiteTheme } from '../../constants/characterThemes';
import { getUserProfilePath } from '../../utils/userPaths';
import { sanitizeHtml } from '../../utils/sanitize';
import sheetStyles from '../ThreadDetail/ThreadDetail.module.css';
import '../ThreadDetail/themes.css';
import styles from './CharacterProfile.module.css';

const formatDate = (value) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const BIO_TABS = [
  { id: 'biography', label: 'Biographie', field: 'biography' },
  { id: 'personality', label: 'Personnalité', field: 'personality' },
  { id: 'appearance', label: 'Apparence', field: 'appearance' },
  { id: 'abilities', label: 'Capacités', field: 'abilities' },
  { id: 'equipment', label: 'Équipements', field: 'equipment' },
  { id: 'weaknesses', label: 'Faiblesses', field: 'weaknesses' },
];

const normalizeSortOrder = (value, fallback = 0) => {
  if (value == null || !Number.isFinite(Number(value))) {
    return fallback;
  }
  const order = Number(value);
  if (order < 0 || order > 999999) {
    return fallback;
  }
  return order;
};

const buildSceneDraft = (threads = []) => {
  const draft = {};
  threads.forEach((thread, index) => {
    draft[thread.threadId] = {
      arcId: thread.arcId ?? '',
      sortOrder: normalizeSortOrder(thread.sortOrder, index),
      chronologyLabel: thread.chronologyLabel ?? '',
    };
  });
  return draft;
};

const buildArcDraft = (arcs = []) =>
  arcs.map((arc, index) => ({
    clientId: String(arc.id),
    id: arc.id,
    name: arc.name,
    sortOrder: arc.sortOrder ?? index,
  }));

const RpSceneCard = ({ thread }) => (
  <article className={styles.rpItem}>
    <Link to={`/threads/${thread.threadSlug}`} className={styles.rpItemMain}>
      <div className={styles.rpItemHeader}>
        {thread.chronologyLabel && (
          <span className={styles.rpChronology}>{thread.chronologyLabel}</span>
        )}
        <h3 className={styles.rpItemTitle}>{thread.title}</h3>
      </div>
      <div className={styles.rpItemMeta}>
        {thread.forum?.name && <span>{thread.forum.name}</span>}
        <span>{thread.replyCount} réponse{thread.replyCount !== 1 ? 's' : ''}</span>
        {thread.lastPostAt && (
          <span>Dernière activité : {formatDate(thread.lastPostAt)}</span>
        )}
      </div>
    </Link>
    {thread.participants?.length > 0 && (
      <div className={styles.rpParticipants}>
        <span className={styles.rpParticipantsLabel}>Avec</span>
        <div className={styles.rpParticipantsList}>
          {thread.participants.map((participant) => (
            <Link
              key={participant.id}
              to={getCharacterProfilePath(participant.id)}
              className={styles.rpParticipant}
            >
              {participant.avatar ? (
                <img src={participant.avatar} alt="" className={styles.rpParticipantAvatar} />
              ) : (
                <span className={styles.rpParticipantFallback}>
                  {(participant.name || '?').charAt(0).toUpperCase()}
                </span>
              )}
              <span>{participant.name}</span>
            </Link>
          ))}
        </div>
      </div>
    )}
  </article>
);

const CharacterProfile = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [activeBioTab, setActiveBioTab] = useState('biography');
  const [organizing, setOrganizing] = useState(false);
  const [draftArcs, setDraftArcs] = useState([]);
  const [draftScenes, setDraftScenes] = useState({});
  const [newArcName, setNewArcName] = useState('');
  const [savingOrganization, setSavingOrganization] = useState(false);
  const [organizationMessage, setOrganizationMessage] = useState('');

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await characterApi.getCharacterProfile(id);
      setProfile(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Personnage introuvable');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProfile();
  }, [id]);

  useCharacterSiteTheme(profile?.character?.sheetTheme);

  const character = profile?.character;
  const availableBioTabs = useMemo(
    () => BIO_TABS.filter((tab) => Boolean(character?.[tab.field])),
    [character]
  );

  const groupedRpScenes = useMemo(() => {
    const arcs = profile?.rpArcs || [];
    const threads = profile?.rpThreads || [];
    const groups = arcs.map((arc) => ({
      arc,
      threads: threads.filter((thread) => thread.arcId === arc.id),
    }));
    const unassigned = threads.filter((thread) => !thread.arcId);
    if (unassigned.length > 0) {
      groups.push({ arc: null, threads: unassigned });
    }
    return groups;
  }, [profile]);

  useEffect(() => {
    if (availableBioTabs.length === 0) return;
    if (!availableBioTabs.some((tab) => tab.id === activeBioTab)) {
      setActiveBioTab(availableBioTabs[0].id);
    }
  }, [availableBioTabs, activeBioTab]);

  const startOrganizing = () => {
    setDraftArcs(buildArcDraft(profile?.rpArcs || []));
    setDraftScenes(buildSceneDraft(profile?.rpThreads || []));
    setNewArcName('');
    setOrganizationMessage('');
    setOrganizing(true);
  };

  const cancelOrganizing = () => {
    setOrganizing(false);
    setOrganizationMessage('');
  };

  const addArc = () => {
    const name = newArcName.trim();
    if (!name) return;
    setDraftArcs((prev) => [
      ...prev,
      {
        clientId: `new-${Date.now()}-${prev.length}`,
        name,
        sortOrder: prev.length,
      },
    ]);
    setNewArcName('');
  };

  const removeArc = (clientId) => {
    setDraftArcs((prev) => prev.filter((arc) => arc.clientId !== clientId));
    setDraftScenes((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((threadId) => {
        if (String(next[threadId].arcId) === String(clientId)) {
          next[threadId] = { ...next[threadId], arcId: '' };
        }
      });
      return next;
    });
  };

  const updateSceneDraft = (threadId, field, value) => {
    setDraftScenes((prev) => ({
      ...prev,
      [threadId]: {
        ...prev[threadId],
        [field]: value,
      },
    }));
  };

  const saveOrganization = async () => {
    setSavingOrganization(true);
    setOrganizationMessage('');
    try {
      const payload = {
        arcs: draftArcs.map((arc, index) => ({
          clientId: arc.clientId,
          id: arc.id,
          name: arc.name,
          sortOrder: index,
        })),
        scenes: (profile?.rpThreads || []).map((thread, index) => ({
          threadId: thread.threadId,
          arcId: draftScenes[thread.threadId]?.arcId || null,
          sortOrder: normalizeSortOrder(draftScenes[thread.threadId]?.sortOrder, index),
          chronologyLabel: draftScenes[thread.threadId]?.chronologyLabel || '',
        })),
      };
      const result = await characterApi.updateRpOrganization(id, payload);
      setProfile((prev) => ({
        ...prev,
        rpArcs: result.rpArcs,
        rpThreads: result.rpThreads,
      }));
      setOrganizing(false);
      setOrganizationMessage('Organisation enregistrée.');
    } catch (err) {
      setOrganizationMessage(err.response?.data?.error || 'Erreur lors de la sauvegarde.');
    } finally {
      setSavingOrganization(false);
    }
  };

  if (loading) {
    return (
      <Layout wide>
        <Loading message="Chargement du personnage..." />
      </Layout>
    );
  }

  if (error || !profile || !character) {
    return (
      <Layout wide>
        <ErrorMessage message={error || 'Personnage introuvable'} onRetry={loadProfile} />
      </Layout>
    );
  }

  const breadcrumbItems = [
    { name: 'Accueil', url: '/', icon: 'home' },
    ...(character.universe ? [{ name: character.universe.name, url: `/univers/${character.universe.slug}` }] : []),
    { name: character.name, url: null, icon: 'character' },
  ];

  const seo = {
    metaTitle: `${character.name} — Earth Forum`,
    metaDescription: character.occupation
      ? `${character.name}, ${character.occupation}`
      : `Fiche personnage de ${character.name} sur Earth Forum`,
    robotsIndex: character.status === 'validated',
  };

  const generalInfoFields = [
    character.universe && { label: 'Univers', value: character.universe.name },
    character.elseworld && { label: 'Elseworld', value: character.elseworld.name },
    character.age && { label: 'Âge', value: character.age },
    character.gender && { label: 'Genre', value: character.gender },
    character.moralAffiliation && { label: 'Affiliation morale', value: character.moralAffiliation },
    character.occupation && { label: 'Occupation', value: character.occupation },
    character.sexualOrientation && { label: 'Orientation sexuelle', value: character.sexualOrientation },
    character.civilStatus && { label: 'Statut civil', value: character.civilStatus },
    character.factions?.length > 0 && { label: 'Factions', value: character.factions.join(', ') },
    character.factionsText && { label: 'Factions (texte)', value: character.factionsText },
    character.actualPseudo && { label: 'Pseudonyme actuel', value: character.actualPseudo },
    character.pseudonyms && { label: 'Autres alias', value: character.pseudonyms },
    character.alias && { label: 'Alias', value: character.alias },
    character.validatedAt && { label: 'Validé le', value: formatDate(character.validatedAt) },
  ].filter(Boolean);

  const sheetPath = getCharacterSheetPath({ sheetThreadSlug: profile.sheetThreadSlug });
  const currentBioTab = availableBioTabs.find((tab) => tab.id === activeBioTab) || availableBioTabs[0];
  const currentTheme = normalizeSheetTheme(character.sheetTheme);

  return (
    <Layout wide>
      <SeoHead seo={seo} />
      <div className={sheetStyles.content}>
        <Breadcrumb items={breadcrumbItems} />

        <div className={sheetStyles.characterThemeScope} data-theme={currentTheme}>
        <div className={sheetStyles.characterHeader}>
          <div className={sheetStyles.characterHeaderWithAvatar}>
            <div className={sheetStyles.characterHeaderAvatar}>
              {character.avatar ? (
                <img src={character.avatar} alt={character.name} />
              ) : (
                <div className={sheetStyles.characterHeaderAvatarPlaceholder} aria-hidden="true">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              )}
            </div>
            <div className={sheetStyles.characterHeaderBody}>
              <div className={sheetStyles.characterHeaderTop}>
                <div className={sheetStyles.characterTitleSection}>
                  <h1 className={sheetStyles.characterTitle}>{character.name}</h1>
                  {(character.firstName || character.lastName) && (
                    <p className={sheetStyles.characterSubtitle}>
                      {[character.firstName, character.lastName].filter(Boolean).join(' ')}
                    </p>
                  )}
                  {character.alias && (
                    <p className={sheetStyles.characterAlias}>{character.alias}</p>
                  )}
                  <p className={styles.playerLine}>
                    {profile.player?.isAnonymous ? (
                      'Joueur : Anonyme'
                    ) : profile.player?.id ? (
                      <>
                        Joueur :{' '}
                        <Link to={getUserProfilePath(profile.player.id)} className={styles.presentationLink}>
                          {profile.player?.pseudo || 'Inconnu'}
                        </Link>
                      </>
                    ) : (
                      `Joueur : ${profile.player?.pseudo || 'Inconnu'}`
                    )}
                  </p>
                  {sheetPath && (
                    <Link to={sheetPath} className={styles.presentationLink}>
                      Fiche de présentation
                    </Link>
                  )}
                </div>
                <div className={sheetStyles.characterHeaderActions}>
                  {character.status && (
                    <span className={`${sheetStyles.statusTag} ${sheetStyles[`status${character.status.charAt(0).toUpperCase() + character.status.slice(1)}`]}`}>
                      {character.status === 'pending' ? 'En attente' :
                       character.status === 'editing' ? 'Points à modifier' :
                       character.status === 'rejected' ? 'Refusée' :
                       character.status === 'draft' ? 'Brouillon' :
                       character.status === 'validated' ? 'Validée' : character.statusMessage || character.status}
                    </span>
                  )}
                  {profile.canEdit && (
                    <Link
                      to={`/characters/${character.id}/edit`}
                      className={sheetStyles.actionBtnCompact}
                      title="Modifier la fiche"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </Link>
                  )}
                </div>
              </div>
              {(character.universe || character.moralAffiliation || character.elseworld) && (
                <div className={sheetStyles.characterMetaRow}>
                  {character.universe && (
                    <span className={sheetStyles.metaTag}>{character.universe.name}</span>
                  )}
                  {character.moralAffiliation && (
                    <span className={sheetStyles.metaTag}>{character.moralAffiliation}</span>
                  )}
                  {character.elseworld && (
                    <span className={sheetStyles.metaTag}>{character.elseworld.name}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{profile.stats?.postCount ?? 0}</span>
            <span className={styles.statLabel}>Messages RP</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{profile.stats?.rpThreadCount ?? 0}</span>
            <span className={styles.statLabel}>Scènes RP</span>
          </div>
          {character.validatedAt && (
            <div className={styles.statCard}>
              <span className={styles.statValue}>{formatDate(character.validatedAt)}</span>
              <span className={styles.statLabel}>Validé</span>
            </div>
          )}
        </div>

        <div className={styles.sectionNav} role="tablist">
          <button
            type="button"
            role="tab"
            className={`${styles.sectionTab} ${activeSection === 'overview' ? styles.sectionTabActive : ''}`}
            onClick={() => setActiveSection('overview')}
          >
            Aperçu
          </button>
          {availableBioTabs.length > 0 && (
            <button
              type="button"
              role="tab"
              className={`${styles.sectionTab} ${activeSection === 'bio' ? styles.sectionTabActive : ''}`}
              onClick={() => setActiveSection('bio')}
            >
              Biographie
            </button>
          )}
          <button
            type="button"
            role="tab"
            className={`${styles.sectionTab} ${activeSection === 'rp' ? styles.sectionTabActive : ''}`}
            onClick={() => setActiveSection('rp')}
          >
            Scènes RP ({profile.rpThreads?.length ?? 0})
          </button>
        </div>

        {activeSection === 'overview' && (
          <div className={sheetStyles.characterSheet}>
            <div className={sheetStyles.characterInfo}>
              {generalInfoFields.length > 0 ? (
                <section className={sheetStyles.generalInfoSection}>
                  <h2 className={sheetStyles.generalInfoTitle}>Informations générales</h2>
                  <dl className={sheetStyles.infoList}>
                    {generalInfoFields.map((field) => (
                      <div key={field.label} className={sheetStyles.infoListRow}>
                        <dt className={sheetStyles.infoLabel}>{field.label}</dt>
                        <dd className={sheetStyles.infoValue}>{field.value}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ) : (
                <p className={styles.emptyState}>Aucune information générale renseignée.</p>
              )}
              {availableBioTabs.length > 0 && (
                <p className={styles.emptyState}>
                  La biographie complète est disponible dans l’onglet « Biographie ».
                </p>
              )}
            </div>
          </div>
        )}

        {activeSection === 'bio' && availableBioTabs.length > 0 && (
          <div className={sheetStyles.characterSheet}>
            <div className={sheetStyles.characterInfo}>
              <div className={sheetStyles.tabsWrapper}>
                <div className={sheetStyles.tabs} role="tablist">
                  {availableBioTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={activeBioTab === tab.id}
                      className={`${sheetStyles.tab} ${activeBioTab === tab.id ? sheetStyles.tabActive : ''}`}
                      onClick={() => setActiveBioTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={sheetStyles.tabContent} role="tabpanel">
                {currentBioTab && character[currentBioTab.field] && (
                  <div className={sheetStyles.characterSection}>
                    <div
                      className={sheetStyles.sectionContent}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(character[currentBioTab.field]) }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeSection === 'rp' && (
          profile.rpThreads?.length > 0 ? (
            <>
              {profile.canOrganizeRpScenes && !organizing && (
                <div className={styles.rpToolbar}>
                  <button type="button" className={styles.organizeButton} onClick={startOrganizing}>
                    Organiser mes scènes
                  </button>
                </div>
              )}

              {organizing && (
                <div className={styles.organizePanel}>
                  <h2 className={styles.organizeTitle}>Organisation des scènes RP</h2>
                  <p className={styles.organizeHint}>
                    Crée des arcs narratifs, assigne tes scènes et définis une chronologie personnelle.
                  </p>

                  <div className={styles.arcEditor}>
                    <h3 className={styles.arcEditorTitle}>Arcs</h3>
                    <div className={styles.addArcRow}>
                      <input
                        type="text"
                        className={styles.organizeInput}
                        value={newArcName}
                        onChange={(event) => setNewArcName(event.target.value)}
                        placeholder="Nom de l’arc"
                      />
                      <button type="button" className={styles.organizeAction} onClick={addArc}>
                        Ajouter
                      </button>
                    </div>
                    {draftArcs.length > 0 ? (
                      <ul className={styles.arcList}>
                        {draftArcs.map((arc) => (
                          <li key={arc.clientId} className={styles.arcListItem}>
                            <input
                              type="text"
                              className={styles.organizeInput}
                              value={arc.name}
                              onChange={(event) => {
                                setDraftArcs((prev) => prev.map((item) => (
                                  item.clientId === arc.clientId
                                    ? { ...item, name: event.target.value }
                                    : item
                                )));
                              }}
                            />
                            <button
                              type="button"
                              className={styles.organizeActionMuted}
                              onClick={() => removeArc(arc.clientId)}
                            >
                              Retirer
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles.organizeEmpty}>Aucun arc défini pour le moment.</p>
                    )}
                  </div>

                  <div className={styles.sceneEditor}>
                    <h3 className={styles.arcEditorTitle}>Scènes</h3>
                    {(profile.rpThreads || []).map((thread) => (
                      <div key={thread.threadId} className={styles.sceneEditorRow}>
                        <div className={styles.sceneEditorTitle}>{thread.title}</div>
                        <input
                          type="text"
                          className={styles.organizeInput}
                          value={draftScenes[thread.threadId]?.chronologyLabel ?? ''}
                          onChange={(event) => updateSceneDraft(thread.threadId, 'chronologyLabel', event.target.value)}
                          placeholder="Chronologie (ex. 003 — Printemps)"
                        />
                        <select
                          className={styles.organizeSelect}
                          value={draftScenes[thread.threadId]?.arcId ?? ''}
                          onChange={(event) => updateSceneDraft(thread.threadId, 'arcId', event.target.value)}
                        >
                          <option value="">Sans arc</option>
                          {draftArcs.map((arc) => (
                            <option key={arc.clientId} value={arc.clientId}>
                              {arc.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <div className={styles.organizeActions}>
                    <button
                      type="button"
                      className={styles.organizeAction}
                      onClick={saveOrganization}
                      disabled={savingOrganization}
                    >
                      {savingOrganization ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                    <button
                      type="button"
                      className={styles.organizeActionMuted}
                      onClick={cancelOrganizing}
                      disabled={savingOrganization}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {organizationMessage && (
                <p className={styles.organizationMessage}>{organizationMessage}</p>
              )}

              <div className={styles.rpGroups}>
                {groupedRpScenes.map((group) => (
                  <section key={group.arc?.id ?? 'unassigned'} className={styles.rpGroup}>
                    <h2 className={styles.rpGroupTitle}>
                      {group.arc ? group.arc.name : 'Autres scènes'}
                    </h2>
                    <div className={styles.rpList}>
                      {group.threads.map((thread) => (
                        <RpSceneCard key={thread.threadId} thread={thread} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </>
          ) : (
            <p className={styles.emptyState}>Aucune scène RP enregistrée pour ce personnage.</p>
          )
        )}
        </div>
      </div>
    </Layout>
  );
};

export default CharacterProfile;
